import { Fragment, useState } from "react";

import { InfinityToggle } from "../../../components/InfinityToggle/InfinityToggle";
import { Input } from "../../../components/Input/Input";
import { holeLabel, ladderErrors, ladderHoles, stepHasErrors } from "./invites";
import { RewardField } from "./RewardField";
import type { InviterRewardStep } from "./types";
import { isEqual } from "./utils";
// The table's silhouette, borrowed whole, as GiftTypesEditor borrows it. Only
// the step columns, the range and the hole rows are ladder-shaped, and those
// are the blocks the mockup marks as new pixels.
import grid from "./HelpTypesEditor.module.css";
import styles from "./InviterRewardsEditor.module.css";

interface Props {
  value: InviterRewardStep[];
  /** The saved ladder, so a box can say it was changed — row by row, as the
   *  save bar has no finer way to name a step. */
  saved: InviterRewardStep[];
  onChange: (next: InviterRewardStep[]) => void;
}

/**
 * Where a new step starts: right after the last one ends, one friend long — or
 * at friend 1. A step with no end has no such number; the new one then starts
 * right after where that step BEGINS, and lights up "The step above has no end"
 * the moment it appears.
 */
function nextStep(steps: InviterRewardStep[]): InviterRewardStep {
  const last = steps[steps.length - 1];
  const end = last ? (last.to ?? last.from) : 0;
  const from = end === undefined ? undefined : end + 1;
  return { from, to: from, name: "", reward: undefined };
}

/**
 * `invites.inviter_rewards` — what the player who shared the code gets for
 * their Nth friend to reach the trigger, one row per run of friends.
 *
 * Mockup: .codelane/artifacts/t-164/config-invites-ladder.html L477-L516, with
 * the empty ladder at L578-L582, the closed one at L589-L602 and the refusals at
 * L609-L639.
 *
 * Between the rows it draws what the rows leave out — the numbers no step
 * covers — because those are numbers the inviter is paid nothing for, and a
 * list of steps alone never says so. Only over a ladder with nothing wrong in
 * it: the server runs a broken ladder as no ladder at all, and holes drawn over
 * it would describe one it does not run.
 */
export function InviterRewardsEditor({ value, saved, onChange }: Props) {
  const errors = ladderErrors(value);
  const holes = errors.some(stepHasErrors) ? null : ladderHoles(value);

  /**
   * Row identities, parallel to `value` and never saved. Keyed by index, the
   * step that slides up into a removed one's place would inherit its ∞
   * toggle — and with it the number that toggle remembers for the step that
   * is gone. A ladder replaced from outside (Discard, the Raw JSON tab) is
   * new rows throughout, which is also what a change of length says it is.
   */
  const [rows, setRows] = useState(() => ({
    ids: value.map((_, i) => i),
    next: value.length,
  }));
  if (rows.ids.length !== value.length) {
    setRows({
      ids: value.map((_, i) => rows.next + i),
      next: rows.next + value.length,
    });
  }

  const patch = (idx: number, next: Partial<InviterRewardStep>) =>
    onChange(value.map((step, i) => (i === idx ? { ...step, ...next } : step)));

  const add = () => {
    setRows({ ids: [...rows.ids, rows.next], next: rows.next + 1 });
    onChange([...value, nextStep(value)]);
  };

  const remove = (idx: number) => {
    setRows({ ids: rows.ids.filter((_, i) => i !== idx), next: rows.next });
    onChange(value.filter((_, i) => i !== idx));
  };

  /** An emptied or half-typed box is blank — `undefined`, never a 0 or a NaN
   *  that would read as a number somebody meant. */
  const friend = (raw: string) => {
    const n = Number(raw);
    return raw === "" || !Number.isFinite(n) ? undefined : n;
  };

  return (
    <>
      <div className={grid.gridEditor}>
        <div className={grid.scroller}>
          <div className={`${grid.row} ${styles.steps} ${grid.head}`}>
            <div>friends</div>
            <div>name</div>
            <div>reward</div>
            <div />
          </div>
          {value.length === 0 && (
            <div className={`${grid.none} ${styles.noneRow}`}>
              No steps — the inviter gets nothing, and nobody&apos;s friends
              are counted toward a ladder.
            </div>
          )}
          {value.map((step, idx) => {
            const n = idx + 1;
            const bad = errors[idx];
            const was = saved[idx];
            // A step the saved ladder does not have yet is changed in every box.
            const changed = (key: keyof InviterRewardStep) =>
              !was || !isEqual(was[key], step[key]);
            const open = step.to === null;
            // The range has one line under it and two boxes over it: the line
            // speaks for the first box that is wrong, and only that box goes
            // red — the other's turn comes once it is fixed.
            const rangeLine = bad.from ?? bad.to;
            const toRed = !bad.from && !!bad.to;
            const hole = holes?.before[idx];
            return (
              <Fragment key={rows.ids[idx] ?? `new-${idx}`}>
                {hole && (
                  <div className={styles.gapRow}>
                    <span className={styles.gapN}>{holeLabel(hole)}</span>
                    inviter gets nothing
                  </div>
                )}
                <div className={`${grid.row} ${styles.steps} ${grid.tall}`}>
                  <div className={grid.cell}>
                    <div className={styles.range}>
                      <div className={styles.from}>
                        <Input
                          className={bad.from ? grid.err : undefined}
                          type="number"
                          min={1}
                          value={step.from ?? ""}
                          onChange={(e) =>
                            patch(idx, { from: friend(e.target.value) })
                          }
                          modified={!bad.from && changed("from")}
                          aria-label={`Step ${n} first friend`}
                          aria-invalid={!!bad.from || undefined}
                        />
                      </div>
                      <span className={styles.dash} aria-hidden>
                        –
                      </span>
                      <div
                        className={`${grid.unitWrap} ${grid.hasNever} ${styles.to}`}
                      >
                        <Input
                          // No unit is ever drawn in this box, so it always
                          // takes the padding that clears the ∞ alone.
                          className={[
                            grid.unitInput,
                            grid.neverInput,
                            toRed ? grid.err : "",
                          ]
                            .filter(Boolean)
                            .join(" ")}
                          // Words are not a number: «No end» needs a text box,
                          // read-only rather than disabled so a keyboard still
                          // reaches the toggle — as a help type's «Never» does.
                          type={open ? "text" : "number"}
                          min={open ? undefined : 1}
                          value={open ? "No end" : (step.to ?? "")}
                          readOnly={open}
                          onChange={(e) =>
                            patch(idx, { to: friend(e.target.value) })
                          }
                          modified={!toRed && changed("to")}
                          aria-label={`Step ${n} last friend`}
                          aria-invalid={toRed || undefined}
                        />
                        <InfinityToggle
                          // The toggle's ∞ is 0; a TYPED 0 is a mistake in a
                          // friend number and must not light it up.
                          value={open ? 0 : step.to || undefined}
                          onChange={(next) =>
                            patch(idx, { to: next === 0 ? null : next })
                          }
                          // A step that arrived open has no number to go back
                          // to: it comes back one friend long.
                          fallback={
                            step.from !== undefined && step.from > 0
                              ? step.from
                              : undefined
                          }
                          fieldLabel={`Step ${n}`}
                          meaning="has no end"
                          className={grid.infNarrow}
                        />
                      </div>
                    </div>
                    {rangeLine ? (
                      <div className={grid.errText}>{rangeLine.row}</div>
                    ) : (
                      open && (
                        <div className={grid.mini}>and every friend after</div>
                      )
                    )}
                  </div>
                  <div className={grid.cell}>
                    <Input
                      className={bad.name ? grid.err : undefined}
                      value={step.name}
                      onChange={(e) => patch(idx, { name: e.target.value })}
                      modified={!bad.name && changed("name")}
                      aria-label={`Step ${n} name`}
                      aria-invalid={!!bad.name || undefined}
                    />
                    {bad.name && (
                      <div className={grid.errText}>{bad.name.row}</div>
                    )}
                  </div>
                  <div className={grid.cell}>
                    <RewardField
                      value={step.reward}
                      onChange={(next) => patch(idx, { reward: next })}
                      error={bad.reward?.row}
                      modified={changed("reward")}
                      aria-label={`Step ${n} reward`}
                    />
                  </div>
                  <button
                    type="button"
                    className={`${grid.rowBtn} ${styles.rowBtn}`}
                    aria-label={`Remove step ${n}`}
                    onClick={() => remove(idx)}
                  >
                    ✕
                  </button>
                </div>
              </Fragment>
            );
          })}
          {holes?.tail != null && (
            <div className={styles.gapRow}>
              <span className={styles.gapN}>{holes.tail}+</span>
              inviter gets nothing from here on
            </div>
          )}
        </div>
        <button
          type="button"
          className={`${grid.addRow} ${styles.addStep}`}
          onClick={add}
        >
          + Add step
        </button>
      </div>
      <div className={styles.helper}>
        Rewards are arbitrary JSON the platform never reads, read at grant time;
        a step&apos;s name is its inbox item&apos;s <code>source_type</code>.
        Editing the ladder changes what the next friends pay — never what was
        already decided.
      </div>
    </>
  );
}
